import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Tag, X, Copy, Check } from 'lucide-react';
import { format } from 'date-fns';
import { cartAPI } from '@/services/api';
import { toast } from 'sonner';

const fmtDate = (val) => {
  if (!val) return null;
  try {
    return format(new Date(val), "do MMM yyyy");
  } catch {
    return null;
  }
};

const CouponPopup = () => {
  const { pathname } = useLocation();
  const [promo, setPromo] = useState(null);
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  const showOnRoute =
    pathname === '/' ||
    pathname === '/webinars' ||
    pathname.startsWith('/webinars/') ||
    pathname === '/industries' ||
    pathname.startsWith('/industries/');

  useEffect(() => {
    let active = true;
    if (!showOnRoute) return;
    cartAPI
      .getActivePromo()
      .then((res) => {
        if (active && res.data && res.data.code) {
          setPromo(res.data);
          setOpen(true);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [pathname, showOnRoute]);

  if (!showOnRoute || !promo || !promo.code) return null;

  const isPct = (promo.type || 'percentage') === 'percentage';
  const discountText = isPct
    ? `${Math.round(promo.discount_value)}% Off`
    : `$${Math.round(promo.discount_value)} Off`;
  const start = fmtDate(promo.starts_at);
  const end = fmtDate(promo.expires_at);

  const copyCode = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(promo.code);
    setCopied(true);
    toast.success('Coupon code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Minimized: floating tag icon (sits above the scroll-to-top button)
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Show discount coupon"
        data-testid="coupon-tag-btn"
        className="fixed bottom-20 right-6 z-[60] w-12 h-12 rounded-full bg-emerald-600 text-white shadow-lg flex items-center justify-center hover:bg-emerald-500 hover:-translate-y-1 transition-all duration-300 animate-fade-in"
      >
        <Tag className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-20 right-6 z-[60] w-[340px] max-w-[calc(100vw-2rem)] rounded-2xl overflow-hidden bg-white border border-zinc-200 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.45)] animate-fade-in"
      data-testid="coupon-popup"
    >
      {/* Header */}
      <div className="bg-zinc-950 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-emerald-400" />
          <span className="font-heading text-lg font-bold text-white">
            {promo.name || 'Instant Discount'}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Minimize coupon"
          data-testid="coupon-minimize-btn"
          className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="p-5 text-center">
        <p className="text-zinc-600 text-sm mb-1">
          {promo.description || 'Purchase any webinar and get'}
        </p>
        <p className="font-heading text-2xl font-bold text-emerald-600 mb-4">
          {discountText}
        </p>

        <button
          type="button"
          onClick={copyCode}
          data-testid="coupon-copy-btn"
          className="group w-full border-2 border-dashed border-emerald-500 rounded-xl py-3 px-4 flex items-center justify-center gap-2 hover:bg-emerald-50 transition-colors"
        >
          <span className="font-mono text-lg font-bold tracking-wider text-zinc-950">
            CODE: {promo.code}
          </span>
          {copied ? (
            <Check className="w-4 h-4 text-emerald-600" />
          ) : (
            <Copy className="w-4 h-4 text-zinc-400 group-hover:text-emerald-600" />
          )}
        </button>

        {(start || end) && (
          <p className="text-zinc-500 text-xs mt-4">
            {start && end
              ? `Validity: ${start} to ${end}`
              : end
              ? `Valid till ${end}`
              : `Valid from ${start}`}
          </p>
        )}
      </div>
    </div>
  );
};

export default CouponPopup;
